<script setup lang="ts">
import { useElementSize } from '@vueuse/core'
import { useSpeechBubbleStore } from '~/stores/speechBubble'

interface Props {
  position?: 'cart' | 'logo' | 'search'
}

const props = withDefaults(defineProps<Props>(), {
  position: 'logo',
})

const speechBubbleStore = useSpeechBubbleStore()
const route = useRoute()
const speechBubbleColor = ref('default')
const speechBubble = ref(null)
const { height } = useElementSize(speechBubble)

let timeoutId: ReturnType<typeof setTimeout> | null = null

function setSpeechBubbleColor(variant: string) {
  switch (variant) {
    case 'default':
      return 'default'
    case 'highlight':
      return 'highlight'
    case 'transaction':
      return 'transaction'
    default:
      return 'default'
  }
}

watch(
  () => speechBubbleStore.variant,
  () => {
    speechBubbleColor.value = setSpeechBubbleColor(speechBubbleStore.variant)
  },
  { immediate: true },
)

watch(
  () => route.fullPath,
  () => {
    if (route.params.sku || route.params.context === 'search')
      return

    speechBubbleStore.hideSpeechBubble()
  },
)

watch(
  () => [speechBubbleStore.message, speechBubbleStore.time],
  () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }

    if (speechBubbleStore.time > 0 && speechBubbleStore.message) {
      timeoutId = setTimeout(() => {
        speechBubbleStore.hideSpeechBubble()
        timeoutId = null
      }, speechBubbleStore.time)
    }
  },
)

watch(
  () => height.value,
  () => {
    speechBubbleStore.setSpeechBubbleHeight(height.value)
  },
)

const isSpeechBubbleHidden = computed(() => {
  return speechBubbleStore.message === ''
})
</script>

<template>
  <div
    aria-live="polite"
  >
    <p
      ref="speechBubble"
      class="speech-bubble"
      :class="[
        speechBubbleColor,
        { hidden: isSpeechBubbleHidden },
        `position-${position}`,
      ]"
      @click="speechBubbleStore.hideSpeechBubble"
    >
      {{ speechBubbleStore.message }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';

.speech-bubble {
  position: absolute;
  top: 5.8rem;
  transform: translateX(-20%);
  font-weight: 400;
  font-size: 1.4rem;
  line-height: 2.4rem;
  border-radius: 2.2rem;
  padding: 0.8rem 1.6rem;
  text-align: center;
  z-index: 4;
  min-width: 232px;

  &::before {
    mask: url('/icons/spike.svg') no-repeat center / contain;
    background-color: var(--bg-color);
    content: '';
    display: block;
    width: 5.9rem;
    height: 1.2rem;
    top: -1.1rem;
    position: absolute;
    transform: translateX(-20%);

    @media screen and (max-width: $tablet) {
      transform: translateX(-22%);
    }
  }
}

// Position near cart icon (right side)
.position-cart {
  top: auto;
  bottom: 0px;
  right: 0rem;
  transform: translate(0%, 100%);

  &::before {
    right: 4.2rem;
    transform: translateX(0%);
  }
}

// Position near search bar (center)
.position-search {
  top: auto;
  bottom: 0px;
  left: 50%;
  transform: translate(-50%, 100%);

  &::before {
    left: 50%;
    transform: translateX(-50%);
  }
}

.default {
  background-color: var(--pink-light);
  --bg-color: var(--pink-light);
}

.highlight {
  background-color: var(--yellow);
  --bg-color: var(--yellow);
}

.transaction {
  background-color: var(--green);
  --bg-color: var(--green);
}

.hidden {
  display: none;
}
</style>
